import React, { useRef, useState } from 'react';
import PartCard from '../Agent/PartCard';
import CartLines from './CartLines';
import MachinePartsSections, { ShopMachine } from './MachinePartsSections';
import { DescribeOutcome, describeOutcome } from './partsDescribe';
import type { MaintenancePart } from '../../hooks/useMachineHub';
import { HubApiError, HubPartIdentification } from '../../services/hubApi';
import { PartCardData } from '../../hooks/useAgentConversation';
import {
  MAX_CART_ITEMS,
  PartsOrderApiError,
  PartsOrderCartItem,
  WidgetAccount,
  WidgetSearchResult,
  cartTotal,
  catalogImageFor,
  copyFor,
  formatCurrency,
  searchPartNumber,
  toCartItem,
} from '../../services/partsOrderApi';
import { displayPartNumber } from '../../utils/displayPartNumber';

interface ShopStepProps {
  organizationId: string;
  codeId: number | null;
  session: string | null;
  customerFirstName?: string;
  cart: PartsOrderCartItem[];
  onAdd: (item: PartsOrderCartItem) => void;
  onUpdateQuantity: (partNumber: string, quantity: number) => void;
  onRemove: (partNumber: string) => void;
  onReview: () => void;
  onVerificationExpired: () => void;
  /** Fired when a search reports the verified phone's accounts (only sent when there are several). */
  onAccounts?: (accounts: WidgetAccount[]) => void;
  // Machine-hub extras: discovery sections for the scanned machine + a quote escape hatch.
  machine?: ShopMachine;
  maintenanceParts?: MaintenancePart[];
  /** Hub only: "Describe it" search. Turns the customer's words into an OEM number (or null); the step then prices it. */
  onDescribe?: (description: string) => Promise<HubPartIdentification>;
  /** Opens the quote form, carrying the words the customer already typed when there are any. */
  onQuoteRequested?: (draft?: string) => void;
}

type Mode = 'number' | 'describe';
const DESCRIBE_MAX = 200; // the identify route's bound

// No location_name: the pickup location isn't chosen until review, so the
// search card shows org-wide stock and the review step shows the per-branch
// number for the chosen location.
function toPartCard(res: WidgetSearchResult, imageUrl: string | null): PartCardData {
  return {
    type: 'part',
    title: displayPartNumber(res.part_number),
    data: {
      part_number: res.part_number,
      description: res.description,
      image_url: imageUrl,
      unit_price: res.customer_unit_price ?? res.list_price,
      location_name: null,
      quantity_at_branch: 0,
      quantity_across_all: res.quantity_across_all,
      availability_status: res.quantity_across_all > 0 ? 'in_stock' : 'vendor_order_needed',
    },
  };
}

function ShopStep({
  organizationId,
  codeId,
  session,
  customerFirstName,
  cart,
  onAdd,
  onUpdateQuantity,
  onRemove,
  onReview,
  onVerificationExpired,
  onAccounts,
  machine,
  maintenanceParts,
  onDescribe,
  onQuoteRequested,
}: ShopStepProps) {
  const [cartExpanded, setCartExpanded] = useState(false);

  const [mode, setMode] = useState<Mode>('number');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [partError, setPartError] = useState<string | null>(null);
  const [partResult, setPartResult] = useState<{
    result: WidgetSearchResult;
    imageUrl: string | null;
  } | null>(null);
  // What to say about the last "Describe it" search.
  const [described, setDescribed] = useState<DescribeOutcome | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const describing = mode === 'describe';

  // The typed text survives a switch (a "part number" that was really a description just moves over).
  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    setPartError(null);
    setPartResult(null);
    setDescribed(null);
  };

  // Live IntelliDealer price + stock for one number; null when it isn't in the system.
  const lookup = async (code: number, partNumber: string) => {
    const result = await searchPartNumber(organizationId, code, partNumber, session);
    if (result.accounts) onAccounts?.(result.accounts);
    if (!result.found) return null;
    return { result, imageUrl: await catalogImageFor(result.part_number) };
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = query.trim();
    if (!text || codeId == null || busy) return;
    setBusy(true);
    setPartError(null);
    setPartResult(null);
    setDescribed(null);
    try {
      if (describing && onDescribe) {
        const id = await onDescribe(text);
        const hit = id.part_number ? await lookup(codeId, id.part_number) : null;
        setPartResult(hit);
        setDescribed(describeOutcome(text, id, hit != null));
      } else {
        const hit = await lookup(codeId, text);
        if (!hit) {
          setPartError(copyFor('part_not_found'));
          return;
        }
        setPartResult(hit);
      }
    } catch (err) {
      if (err instanceof PartsOrderApiError && err.code === 'verification_required') {
        onVerificationExpired();
        return;
      }
      if (err instanceof HubApiError && err.code === 'http_401') {
        onVerificationExpired();
        return;
      }
      // Only our own errors carry customer copy; a dropped connection would otherwise print "Load failed".
      setPartError(err instanceof PartsOrderApiError || err instanceof HubApiError ? err.message : copyFor(undefined));
    } finally {
      setBusy(false);
    }
  };

  // After adding, clear the search so the next part number can go straight in.
  const handleAddSearched = () => {
    if (!partResult) return;
    const { result, imageUrl } = partResult;
    if (cart.length >= MAX_CART_ITEMS && !cart.some((i) => i.part_number === result.part_number)) {
      setPartError(`Carts are limited to ${MAX_CART_ITEMS} different parts. Remove something first.`);
      return;
    }
    onAdd(toCartItem(result, imageUrl));
    setPartResult(null);
    setDescribed(null);
    setQuery('');
    searchInputRef.current?.focus();
  };

  // The quote draft is whatever is in the field now (refined after a miss, maybe), never a part number.
  const askForQuote = () => onQuoteRequested?.(describing ? query.trim() || undefined : undefined);

  const itemCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const total = cartTotal(cart);

  return (
    <div className="form-step">
      <h2>Find Your Parts</h2>
      <p className="step-intro">
        {onDescribe
          ? 'Search by part number, or describe what you need and we will find the number for you.'
          : 'Search by part number to see live pricing and stock, then add each part to your cart.'}
      </p>
      {customerFirstName && (
        <p className="parts-order-welcome">
          ✓ Welcome back, {customerFirstName} — your account pricing is applied.
        </p>
      )}

      {onDescribe && (
        <div className="parts-order-mode" role="group" aria-label="Search by">
          <button type="button" className={describing ? '' : 'active'} aria-pressed={!describing} disabled={busy} onClick={() => switchMode('number')}>
            Part number
          </button>
          <button type="button" className={describing ? 'active' : ''} aria-pressed={describing} disabled={busy} onClick={() => switchMode('describe')}>
            Describe it
          </button>
        </div>
      )}
      <form className="parts-order-search" onSubmit={handleSearch}>
        <input
          ref={searchInputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={describing ? 'e.g. battery, oil filter, mower blade' : 'Enter an exact part number'}
          aria-label={describing ? 'Describe the part' : 'Part number'}
          enterKeyHint="search"
          minLength={describing ? 3 : undefined}
          maxLength={describing ? DESCRIBE_MAX : 40}
          // Part numbers: caps keyboard, no autocorrect (iOS "fixes" TY26498B otherwise).
          autoCapitalize={describing ? 'sentences' : 'characters'}
          autoCorrect={describing ? 'on' : 'off'}
          spellCheck={describing}
          disabled={busy}
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !query.trim()}>
          {busy ? (describing ? 'Finding…' : 'Searching…') : describing ? 'Find my part' : 'Search'}
        </button>
      </form>
      {busy && describing && (
        <p className="parts-order-status" role="status">Finding the right part{machine ? ` for your ${machine.model}` : ''}. This takes a few seconds.</p>
      )}
      {partError && <div className="error-text">{partError}</div>}
      {partResult && (
        <div className="parts-order-results">
          {described?.heading && <p className="parts-order-results__heading">{described.heading}</p>}
          <PartCard card={toPartCard(partResult.result, partResult.imageUrl)} onAddToCart={handleAddSearched} />
        </div>
      )}
      {described?.note && (
        <div className={`parts-order-describe-note${described.kind === 'miss' ? ' parts-order-describe-note--miss' : ''}`}>
          <p>{described.note}</p>
          {onQuoteRequested && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={askForQuote}>Ask for a quote</button>
          )}
        </div>
      )}
      {(machine || (maintenanceParts && maintenanceParts.length > 0)) && (
        <MachinePartsSections
          organizationId={organizationId}
          codeId={codeId}
          session={session}
          machine={machine}
          maintenanceParts={maintenanceParts}
          onAdd={onAdd}
          onVerificationExpired={onVerificationExpired}
          onAccounts={onAccounts}
        />
      )}
      {onQuoteRequested && (
        <button type="button" className="hub-link hub-link-btn" onClick={askForQuote}>Not sure of the part number? Ask for a quote</button>
      )}

      {cart.length > 0 && (
        <div className={`cart-panel parts-order-cart-bar${cartExpanded ? ' cart-panel--expanded' : ''}`}>
          <button
            type="button"
            className="cart-panel__summary"
            onClick={() => setCartExpanded((v) => !v)}
            aria-expanded={cartExpanded}
          >
            <span className="cart-panel__summary-left">
              <span className="cart-panel__label">Cart</span>
              <span className="cart-panel__count">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
            </span>
            <span className="cart-panel__summary-right">
              {total != null && <span className="cart-panel__total">{formatCurrency(total)}</span>}
              <svg
                className="cart-panel__chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>

          {cartExpanded && (
            <div className="cart-panel__body">
              <CartLines items={cart} onUpdateQuantity={onUpdateQuantity} onRemove={onRemove} />
            </div>
          )}

          <div className="cart-panel__actions">
            <button type="button" className="cart-panel__btn cart-panel__btn--primary" onClick={onReview}>
              Review order
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShopStep;
